import React, { FunctionComponent, useEffect } from 'react';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { PrimaryButton } from 'app/common/components/controls/button/button';

// eslint-disable-next-line @trello/less-matches-component
import styles from './AutomaticOnboardingOverlay.less';
import api from 'app/gamma/src/api';
import { ProfileSyncResponse } from 'app/gamma/src/types/responses';
import { useAlert, AlertContainer } from 'app/gamma/src/components/alert';

import { AtlassianAccountOnboardingLinks } from '../../AtlassianAccountOnboardingLinks';
import { OnboardingDialog } from '../../v2Overlays/OnboardingDialog';

const ATLASSIAN_PROFILE_SYNC_ALERT_AREA = 'ATLASSIAN_PROFILE_SYNC_ALERT_AREA';
const ATLASSIAN_PROFILE_SYNC_ALERT_NAME = 'ATLASSIAN_PROFILE_SYNC_ALERT_NAME';

const format = forNamespace(['aa onboarding', 'dialog'], {
  shouldEscapeStrings: false,
});
const formatError = forTemplate('app_management');

/**
 * Extracted to allow testing/storybooking this component.
 * Not expecting this property to be overridden by consumers.
 */
const defaultProfileSyncResponseProvider = () => {
  return api.client.rest.post<ProfileSyncResponse>(
    'members/me/atlassianAccount/autoOnboarding',
    {
      query: {
        returnUrl: window.location.pathname,
      },
    },
  );
};
interface EmailMismatchProps {
  aaEmail: string;
  orgName?: string;
  onDismiss: () => void;
  onClickLogin: () => void;
  onTrackScreenEvent: () => void;
}

export const EmailMismatch: FunctionComponent<EmailMismatchProps> = ({
  aaEmail,
  orgName,
  onDismiss,
  onClickLogin,
  onTrackScreenEvent,
}) => {
  const { showAlert: showErrorAlert } = useAlert({
    alertArea: ATLASSIAN_PROFILE_SYNC_ALERT_AREA,
    alertName: ATLASSIAN_PROFILE_SYNC_ALERT_NAME,
  });

  const atlassianLogin = async () => {
    try {
      const response = await defaultProfileSyncResponseProvider();
      window.location.assign(response.redirect_url);
      onClickLogin();
    } catch (err) {
      showErrorAlert();
    }
  };

  useEffect(() => {
    onTrackScreenEvent();
  }, [onTrackScreenEvent]);

  return (
    <>
      <AlertContainer
        displayType="error"
        name={ATLASSIAN_PROFILE_SYNC_ALERT_NAME}
        area={ATLASSIAN_PROFILE_SYNC_ALERT_AREA}
      >
        {formatError('something-went-wrong')}
      </AlertContainer>
      <OnboardingDialog
        title={format('error title')}
        subtitle={aaEmail}
        helpLink={AtlassianAccountOnboardingLinks.Help}
        closeDialog={onDismiss}
        className={styles.emailMismatchTitle}
        showError={true}
      >
        <div className={styles.emailMismatchContent}>
          <p>
            {format('error main message', {
              aaEmail: <strong key="aaEmail">{aaEmail}</strong>,
              orgDomain: orgName,
            })}
          </p>
          <p>{format('error second message')}</p>
        </div>
        <div className={styles.section}>
          <PrimaryButton onClick={atlassianLogin}>
            {format('log in to atlassian')}
          </PrimaryButton>
        </div>
      </OnboardingDialog>
    </>
  );
};
